/**
 * Counts do toggle/reconciliar/disconnect: matriz autoritativa (RPC de modo),
 * cache ws:user, fallback legado com teto. Shadow só em fetch com integrações.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { getGlobalSingleton } from "@/lib/global-singleton";
import { INBOX_SCOPE_CHANGED_EVENT, type InboxScopeChangedDetail } from "./inbox-scope-changed";
import {
  deriveChannelCountsParityVector,
  deriveCountForIntegration,
  deriveUnassignedCount,
  hashChannelCountsParityVector,
  hasUsableMatrixRows,
  type ChannelCountsEffective,
  type InboxChannel,
  type InboxStatsRow,
} from "./inbox-stats-derive";
import { isInboxChannelCountsMatrixForcedLegacy } from "./inbox-stats-flags";
import {
  countConversationsForIntegration,
  countUnassignedConversationsForChannel,
} from "@/lib/conversation-integration-assign";

const FRESH_TTL_MS = 10_000;
const ROLLOUT_FRESH_MS = 15_000;
const ROLLOUT_GRACE_MS = 45_000;
const LEGACY_WINDOW_MS = 45_000;
const SHADOW_COOLDOWN_MS = 8 * 60_000;
const SHADOW_SAMPLE_RATE = 0.1;
const REALTIME_DEBOUNCE_MS = 2_000;

export const INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT = "inbox-channel-counts-invalidated";

export type InboxChannelCountsInvalidatedDetail = {
  workspaceId?: string;
  kind: "hard" | "soft";
};

type MatrixSnap = {
  rows: InboxStatsRow[];
  authoritative: boolean;
  fetchedAt: number;
  workspaceId: string;
  userId: string;
};

type CacheState = {
  lastKnownRollout: Map<string, { value: ChannelCountsEffective; at: number }>;
  rolloutInflight: Map<string, Promise<ChannelCountsEffective>>;
  matrixFresh: Map<string, MatrixSnap>;
  lastKnownMatrix: Map<string, MatrixSnap>;
  matrixHardBlocked: Set<string>;
  matrixInflight: Map<string, Promise<InboxStatsRow[] | null>>;
  legacyCircuitUntil: Map<string, number>;
  legacyUnassigned: Map<string, { value: Record<InboxChannel, number>; at: number }>;
  legacyIntegrations: Map<string, { value: Record<string, number>; at: number }>;
  legacyInflight: Map<string, Promise<unknown>>;
  shadowLastAt: Map<string, number>;
  shadowInflight: Set<string>;
  softDebounce: Map<string, ReturnType<typeof setTimeout>>;
  invalidateGen: Map<string, number>;
  listenersInstalled: boolean;
};

function cache(): CacheState {
  return getGlobalSingleton<CacheState>("__fabInboxChannelCounts__", () => ({
    lastKnownRollout: new Map(),
    rolloutInflight: new Map(),
    matrixFresh: new Map(),
    lastKnownMatrix: new Map(),
    matrixHardBlocked: new Set(),
    matrixInflight: new Map(),
    legacyCircuitUntil: new Map(),
    legacyUnassigned: new Map(),
    legacyIntegrations: new Map(),
    legacyInflight: new Map(),
    shadowLastAt: new Map(),
    shadowInflight: new Set(),
    softDebounce: new Map(),
    invalidateGen: new Map(),
    listenersInstalled: false,
  }));
}

function scopeKey(workspaceId: string, userId: string): string {
  return `${workspaceId}:${userId}`;
}

function sortedIdsHash(ids: string[]): string {
  return [...ids].sort().join(",");
}

function generation(key: string): number {
  return cache().invalidateGen.get(key) ?? 0;
}

function bumpGeneration(key: string): number {
  const s = cache();
  const next = generation(key) + 1;
  s.invalidateGen.set(key, next);
  return next;
}

function clearInflightForScope(key: string): void {
  const s = cache();
  s.rolloutInflight.delete(key);
  s.matrixInflight.delete(key);
  s.legacyInflight.delete(`u:${key}`);
  for (const inflightKey of [...s.legacyInflight.keys()]) {
    if (inflightKey.startsWith(`${key}:`)) s.legacyInflight.delete(inflightKey);
  }
}

function dispatchChannelCountsInvalidated(detail: InboxChannelCountsInvalidatedDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(INBOX_CHANNEL_COUNTS_INVALIDATED_EVENT, { detail }));
}

export { hasUsableMatrixRows } from "./inbox-stats-derive";

export function hardInvalidateInboxChannelCounts(workspaceId?: string): void {
  const s = cache();
  const clearFor = (key: string) => {
    bumpGeneration(key);
    clearInflightForScope(key);
    s.matrixFresh.delete(key);
    s.lastKnownMatrix.delete(key);
    s.matrixHardBlocked.add(key);
    s.legacyUnassigned.delete(key);
    for (const k of [...s.legacyIntegrations.keys()]) {
      if (k.startsWith(`${key}:`)) s.legacyIntegrations.delete(k);
    }
    s.legacyCircuitUntil.delete(key);
    s.lastKnownRollout.delete(key);
  };

  if (!workspaceId) {
    for (const key of [
      ...s.matrixFresh.keys(),
      ...s.lastKnownMatrix.keys(),
      ...s.lastKnownRollout.keys(),
      ...s.rolloutInflight.keys(),
      ...s.matrixInflight.keys(),
      ...s.legacyUnassigned.keys(),
    ]) {
      bumpGeneration(key);
    }
    s.rolloutInflight.clear();
    s.matrixInflight.clear();
    s.legacyInflight.clear();
    s.matrixFresh.clear();
    s.lastKnownMatrix.clear();
    s.matrixHardBlocked.clear();
    s.legacyUnassigned.clear();
    s.legacyIntegrations.clear();
    s.legacyCircuitUntil.clear();
    s.lastKnownRollout.clear();
    dispatchChannelCountsInvalidated({ kind: "hard" });
    return;
  }

  const keys = new Set<string>();
  for (const key of [
    ...s.lastKnownMatrix.keys(),
    ...s.matrixFresh.keys(),
    ...s.lastKnownRollout.keys(),
    ...s.legacyUnassigned.keys(),
  ]) {
    if (key.startsWith(`${workspaceId}:`)) keys.add(key);
  }
  for (const key of keys) clearFor(key);
  dispatchChannelCountsInvalidated({ workspaceId, kind: "hard" });
}

export function softInvalidateInboxChannelCounts(workspaceId: string): void {
  const s = cache();
  for (const [key, snap] of s.matrixFresh) {
    if (key.startsWith(`${workspaceId}:`)) {
      s.matrixFresh.delete(key);
      if (!s.matrixHardBlocked.has(key)) s.lastKnownMatrix.set(key, snap);
    }
  }
  for (const key of [...s.legacyUnassigned.keys()]) {
    if (key.startsWith(`${workspaceId}:`)) s.legacyUnassigned.delete(key);
  }
  for (const key of [...s.legacyIntegrations.keys()]) {
    if (key.includes(`:${workspaceId}:`) || key.startsWith(`${workspaceId}:`)) {
      s.legacyIntegrations.delete(key);
    }
  }
  dispatchChannelCountsInvalidated({ workspaceId, kind: "soft" });
}

export function noteConversationChangeForChannelCounts(
  workspaceId: string,
  payload: { eventType?: string; old?: Record<string, unknown>; new?: Record<string, unknown> },
): void {
  const event = payload.eventType;
  if (event === "INSERT" || event === "DELETE") {
    scheduleSoftInvalidate(workspaceId);
    return;
  }
  if (event !== "UPDATE") return;
  const prev = payload.old ?? {};
  const next = payload.new ?? {};
  if (Object.keys(prev).length === 0) {
    scheduleSoftInvalidate(workspaceId);
    return;
  }
  const watched = ["integration_id", "channel", "is_closed"] as const;
  const canDiff = watched.some((col) => col in prev);
  if (!canDiff) return;
  const changed = watched.some((col) => col in prev && col in next && prev[col] !== next[col]);
  if (changed) scheduleSoftInvalidate(workspaceId);
}

function scheduleSoftInvalidate(workspaceId: string): void {
  const s = cache();
  const prev = s.softDebounce.get(workspaceId);
  if (prev) clearTimeout(prev);
  s.softDebounce.set(
    workspaceId,
    setTimeout(() => {
      s.softDebounce.delete(workspaceId);
      softInvalidateInboxChannelCounts(workspaceId);
    }, REALTIME_DEBOUNCE_MS),
  );
}

function installInvalidationListeners(): void {
  const s = cache();
  if (s.listenersInstalled || typeof window === "undefined") return;
  s.listenersInstalled = true;

  window.addEventListener(INBOX_SCOPE_CHANGED_EVENT, (event) => {
    const detail = (event as CustomEvent<InboxScopeChangedDetail>).detail;
    hardInvalidateInboxChannelCounts(detail?.workspaceId);
  });

  supabase.auth.onAuthStateChange((event) => {
    if (event === "SIGNED_OUT" || event === "SIGNED_IN") {
      hardInvalidateInboxChannelCounts();
    }
  });
}

function mapMatrixRow(row: Record<string, unknown>): InboxStatsRow {
  return {
    channel: String(row.channel ?? "") as InboxChannel,
    integrationId: (row.integration_id as string | null) ?? null,
    open: Number(row.open_count ?? 0),
    closed: Number(row.closed_count ?? 0),
    unreadOpen: Number(row.unread_open_count ?? 0),
    unreadClosed: Number(row.unread_closed_count ?? 0),
    mine: Number(row.mine_count ?? 0),
    attention: Number(row.attention_count ?? 0),
    ia: Number(row.ia_count ?? 0),
    mineClosed: Number(row.mine_closed_count ?? 0),
    attentionClosed: Number(row.attention_closed_count ?? 0),
    iaClosed: Number(row.ia_closed_count ?? 0),
  };
}

async function fetchMatrixRows(
  client: SupabaseClient<Database>,
  workspaceId: string,
): Promise<InboxStatsRow[]> {
  const { data, error } = await (
    client.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: unknown }>
  )("get_inbox_stats_v2", { p_workspace_id: workspaceId });
  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(mapMatrixRow);
}

async function currentUserId(client: SupabaseClient<Database>): Promise<string | null> {
  const { data } = await client.auth.getUser();
  return data.user?.id ?? null;
}

function parseConfig(data: unknown): ChannelCountsEffective | null {
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
  if (!row) return null;
  const mode = row.mode;
  if (mode !== "legacy" && mode !== "matrix") return null;
  return { mode, shadow: row.shadow === true };
}

async function getEffectiveConfig(
  client: SupabaseClient<Database>,
  workspaceId: string,
  userId: string,
): Promise<ChannelCountsEffective> {
  if (isInboxChannelCountsMatrixForcedLegacy()) return { mode: "legacy", shadow: false };

  const s = cache();
  const key = scopeKey(workspaceId, userId);
  const known = s.lastKnownRollout.get(key);
  if (known && Date.now() - known.at < ROLLOUT_FRESH_MS) return known.value;

  const inflight = s.rolloutInflight.get(key);
  if (inflight) return inflight;

  const pending = (async (): Promise<ChannelCountsEffective> => {
    const genAtStart = generation(key);
    try {
      const { data, error } = await (
        client.rpc as unknown as (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{ data: unknown; error: unknown }>
      )("get_channel_counts_config", { p_workspace_id: workspaceId });
      if (generation(key) !== genAtStart) {
        return known?.value ?? { mode: "legacy", shadow: false };
      }
      if (!error) {
        const parsed = parseConfig(data);
        if (parsed) {
          s.lastKnownRollout.set(key, { value: parsed, at: Date.now() });
          return parsed;
        }
      }
    } catch {
      /* lastKnown / legacy */
    }
    if (generation(key) !== genAtStart) {
      return known?.value ?? { mode: "legacy", shadow: false };
    }
    if (known && Date.now() - known.at < ROLLOUT_GRACE_MS) return known.value;
    const fallback: ChannelCountsEffective = { mode: "legacy", shadow: false };
    s.lastKnownRollout.set(key, { value: fallback, at: Date.now() });
    return fallback;
  })();

  s.rolloutInflight.set(key, pending);
  try {
    return await pending;
  } finally {
    s.rolloutInflight.delete(key);
  }
}

async function loadMatrixRows(
  client: SupabaseClient<Database>,
  workspaceId: string,
  userId: string,
  authoritative: boolean,
): Promise<InboxStatsRow[] | null> {
  const s = cache();
  const key = scopeKey(workspaceId, userId);
  const fresh = s.matrixFresh.get(key);
  if (fresh && Date.now() - fresh.fetchedAt < FRESH_TTL_MS) return fresh.rows;

  const inflight = s.matrixInflight.get(key);
  if (inflight) return inflight;

  const pending = (async (): Promise<InboxStatsRow[] | null> => {
    const genAtStart = generation(key);
    try {
      const rows = await fetchMatrixRows(client, workspaceId);
      if (generation(key) !== genAtStart || s.matrixHardBlocked.has(key)) {
        return null;
      }
      const snap: MatrixSnap = {
        rows,
        authoritative,
        fetchedAt: Date.now(),
        workspaceId,
        userId,
      };
      s.matrixFresh.set(key, snap);
      s.lastKnownMatrix.set(key, snap);
      s.matrixHardBlocked.delete(key);
      return rows;
    } catch (error) {
      console.warn("[inbox-channel-counts] falha get_inbox_stats_v2", error);
      if (generation(key) !== genAtStart || s.matrixHardBlocked.has(key)) {
        return null;
      }
      const stale = s.lastKnownMatrix.get(key);
      if (stale) return stale.rows;
      return null;
    }
  })();

  s.matrixInflight.set(key, pending);
  try {
    return await pending;
  } finally {
    s.matrixInflight.delete(key);
  }
}

function maybeShadow(
  client: SupabaseClient<Database>,
  workspaceId: string,
  userId: string,
  rows: InboxStatsRow[],
  integrationIds: Array<{ channel: InboxChannel; integrationId: string }>,
): void {
  const s = cache();
  const key = scopeKey(workspaceId, userId);
  if (s.shadowInflight.has(key)) return;
  const last = s.shadowLastAt.get(key) ?? 0;
  if (Date.now() - last < SHADOW_COOLDOWN_MS) return;
  if (Math.random() > SHADOW_SAMPLE_RATE) return;

  s.shadowInflight.add(key);
  s.shadowLastAt.set(key, Date.now());
  void (async () => {
    try {
      const matrixVector = deriveChannelCountsParityVector(rows, integrationIds);
      const unassigned = {
        whatsapp: await countUnassignedConversationsForChannel(workspaceId, "whatsapp"),
        instagram: await countUnassignedConversationsForChannel(workspaceId, "instagram"),
        messenger: await countUnassignedConversationsForChannel(workspaceId, "messenger"),
      };
      const integrations: ChannelCountsParityVectorIntegrations = [];
      for (const item of integrationIds) {
        integrations.push({
          ...item,
          count: await countConversationsForIntegration(workspaceId, item.integrationId),
        });
      }
      const legacyVector = { unassigned, integrations };
      const matched = hashChannelCountsParityVector(matrixVector) === hashChannelCountsParityVector(legacyVector);
      console.info("[inbox-channel-counts][shadow]", {
        workspaceId,
        userId,
        matrix_hash: hashChannelCountsParityVector(matrixVector),
        legacy_hash: hashChannelCountsParityVector(legacyVector),
        matched,
        ts: new Date().toISOString(),
      });
    } catch {
      /* shadow nunca afeta a UI */
    } finally {
      s.shadowInflight.delete(key);
    }
  })();
}

type ChannelCountsParityVectorIntegrations = Array<{
  channel: InboxChannel;
  integrationId: string;
  count: number;
}>;

async function legacyUnassignedBurst(
  workspaceId: string,
  userId: string,
): Promise<Record<InboxChannel, number>> {
  const s = cache();
  const key = scopeKey(workspaceId, userId);
  const cached = s.legacyUnassigned.get(key);
  if (cached && Date.now() - cached.at < LEGACY_WINDOW_MS) return cached.value;

  const circuitUntil = s.legacyCircuitUntil.get(key) ?? 0;
  if (Date.now() < circuitUntil && cached) return cached.value;

  const flightKey = `u:${key}`;
  const inflight = s.legacyInflight.get(flightKey) as Promise<Record<InboxChannel, number>> | undefined;
  if (inflight) return inflight;

  const pending = (async () => {
    const genAtStart = generation(key);
    const value = {
      whatsapp: await countUnassignedConversationsForChannel(workspaceId, "whatsapp"),
      instagram: await countUnassignedConversationsForChannel(workspaceId, "instagram"),
      messenger: await countUnassignedConversationsForChannel(workspaceId, "messenger"),
    };
    if (generation(key) !== genAtStart) {
      return s.legacyUnassigned.get(key)?.value ?? value;
    }
    s.legacyUnassigned.set(key, { value, at: Date.now() });
    s.legacyCircuitUntil.set(key, Date.now() + LEGACY_WINDOW_MS);
    return value;
  })();

  s.legacyInflight.set(flightKey, pending);
  try {
    return await pending;
  } finally {
    s.legacyInflight.delete(flightKey);
  }
}

async function legacyIntegrationBurst(
  workspaceId: string,
  userId: string,
  channel: InboxChannel,
  integrationIds: string[],
): Promise<Record<string, number>> {
  const s = cache();
  const key = `${scopeKey(workspaceId, userId)}:${channel}:${sortedIdsHash(integrationIds)}`;
  const cached = s.legacyIntegrations.get(key);
  if (cached && Date.now() - cached.at < LEGACY_WINDOW_MS) return cached.value;

  const scope = scopeKey(workspaceId, userId);
  const circuitUntil = s.legacyCircuitUntil.get(scope) ?? 0;
  if (Date.now() < circuitUntil && cached) return cached.value;

  const inflight = s.legacyInflight.get(key) as Promise<Record<string, number>> | undefined;
  if (inflight) return inflight;

  const pending = (async () => {
    const genAtStart = generation(scope);
    const value: Record<string, number> = {};
    await Promise.all(
      integrationIds.map(async (id) => {
        value[id] = await countConversationsForIntegration(workspaceId, id);
      }),
    );
    if (generation(scope) !== genAtStart) {
      return s.legacyIntegrations.get(key)?.value ?? value;
    }
    s.legacyIntegrations.set(key, { value, at: Date.now() });
    s.legacyCircuitUntil.set(scope, Date.now() + LEGACY_WINDOW_MS);
    return value;
  })();

  s.legacyInflight.set(key, pending);
  try {
    return await pending;
  } finally {
    s.legacyInflight.delete(key);
  }
}

async function resolveRows(
  client: SupabaseClient<Database>,
  workspaceId: string,
  userId: string,
): Promise<{ rows: InboxStatsRow[] | null; config: ChannelCountsEffective }> {
  installInvalidationListeners();
  const config = await getEffectiveConfig(client, workspaceId, userId);
  if (config.mode !== "matrix") return { rows: null, config };
  const rows = await loadMatrixRows(client, workspaceId, userId, true);
  return { rows, config };
}

export async function fetchChannelUnassignedCounts(
  client: SupabaseClient<Database>,
  workspaceId: string,
): Promise<Record<InboxChannel, number>> {
  const userId = await currentUserId(client);
  if (!userId) {
    return { whatsapp: 0, instagram: 0, messenger: 0 };
  }

  const { rows, config } = await resolveRows(client, workspaceId, userId);
  if (hasUsableMatrixRows(rows)) {
    return {
      whatsapp: deriveUnassignedCount(rows, "whatsapp"),
      instagram: deriveUnassignedCount(rows, "instagram"),
      messenger: deriveUnassignedCount(rows, "messenger"),
    };
  }
  if (rows !== null && config.mode === "matrix") {
    console.warn("[inbox-channel-counts] matriz vazia, usando legado", { workspaceId });
  }

  return legacyUnassignedBurst(workspaceId, userId);
}

export async function fetchChannelConversationCounts(
  client: SupabaseClient<Database>,
  params: {
    workspaceId: string;
    channel: InboxChannel;
    integrationIds: string[];
  },
): Promise<{ unassigned: number; byIntegrationId: Record<string, number> }> {
  const { workspaceId, channel, integrationIds } = params;
  const userId = await currentUserId(client);
  if (!userId) return { unassigned: 0, byIntegrationId: {} };

  const { rows, config } = await resolveRows(client, workspaceId, userId);
  if (hasUsableMatrixRows(rows)) {
    const byIntegrationId: Record<string, number> = {};
    for (const id of integrationIds) {
      byIntegrationId[id] = deriveCountForIntegration(rows, channel, id);
    }
    if (config.shadow) {
      maybeShadow(
        client,
        workspaceId,
        userId,
        rows,
        integrationIds.map((integrationId) => ({ channel, integrationId })),
      );
    }
    return { unassigned: deriveUnassignedCount(rows, channel), byIntegrationId };
  }
  if (rows !== null && config.mode === "matrix") {
    console.warn("[inbox-channel-counts] matriz vazia, usando legado", { workspaceId, channel });
  }

  const [unassignedMap, byIntegrationId] = await Promise.all([
    legacyUnassignedBurst(workspaceId, userId).then((all) => all[channel]),
    integrationIds.length
      ? legacyIntegrationBurst(workspaceId, userId, channel, integrationIds)
      : Promise.resolve({} as Record<string, number>),
  ]);
  return { unassigned: unassignedMap, byIntegrationId };
}

export async function fetchIntegrationConversationCount(
  client: SupabaseClient<Database>,
  params: { workspaceId: string; channel: InboxChannel; integrationId: string },
): Promise<number> {
  const { byIntegrationId } = await fetchChannelConversationCounts(client, {
    workspaceId: params.workspaceId,
    channel: params.channel,
    integrationIds: [params.integrationId],
  });
  return byIntegrationId[params.integrationId] ?? 0;
}
